// ChoirDisplay.tsx - Public component for displaying choirs with modern card design and filtering
import React, { useState, useEffect } from 'react';
import { Choir, ChoirImage } from '../../types/Choir';
import ChoirService from '../../services/ChoirService';
import { Music, Users, Star, Clock, ChevronLeft, ChevronRight } from 'lucide-react';

// Carousel component for multiple images
interface ImageCarouselProps {
  images: ChoirImage[];
  alt: string;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({ images, alt }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const getImageUrl = (image: ChoirImage): string => {
    // Use S3 URL if available, otherwise fall back to base64 (for backward compatibility)
    if (image.s3Url) {
      return image.s3Url;
    }
    // Fallback for legacy base64 images (stored in s3Key field for backward compatibility)
    if (image.s3Key && image.s3Key.startsWith('data:')) {
      return image.s3Key;
    }
    // If no valid URL, return a placeholder or empty string
    return '';
  };

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  if (images.length === 1) {
    const imageUrl = getImageUrl(images[0]);
    if (!imageUrl) return null;

    return (
      <img
        src={imageUrl}
        alt={alt}
        className="w-full h-full object-cover"
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = 'none';
        }}
      />
    );
  }

  const currentImageUrl = getImageUrl(images[currentIndex]);
  if (!currentImageUrl) return null;

  return (
    <div className="relative w-full h-full group">
      <img
        src={currentImageUrl}
        alt={`${alt} - Image ${currentIndex + 1}`}
        className="w-full h-full object-cover transition-opacity duration-300"
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = 'none';
        }}
      />

      {/* Navigation buttons */}
      <button
        onClick={goToPrevious}
        className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        aria-label="Previous image"
      >
        <ChevronLeft size={20} />
      </button>

      <button
        onClick={goToNext}
        className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        aria-label="Next image"
      >
        <ChevronRight size={20} />
      </button>

      {/* Dots indicator */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-colors duration-200 ${
              index === currentIndex ? 'bg-white' : 'bg-white/50'
            }`}
            aria-label={`Go to image ${index + 1}`}
          />
        ))}
      </div>

      {/* Image counter */}
      <div className="absolute top-4 right-4 bg-black/50 text-white px-2 py-1 rounded text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        {currentIndex + 1} / {images.length}
      </div>
    </div>
  );
};

const ChoirDisplay: React.FC = () => {
  const [choirs, setChoirs] = useState<Choir[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [filteredChoirs, setFilteredChoirs] = useState<Choir[]>([]);

  useEffect(() => {
    const loadChoirs = async () => {
      try {
        setLoading(true);
        const choirsData = await ChoirService.getAllActiveChoirs();
        setChoirs(choirsData);
        setFilteredChoirs(choirsData);
      } catch (err) {
        console.error('Failed to load choirs:', err);
        setError('Failed to load choirs');
      } finally {
        setLoading(false);
      }
    };

    loadChoirs();
  }, []);

  // Get filter tabs based on choir names
  const getFilterTabs = (): string[] => {
    const choirNames = choirs.map(choir => choir.name);
    return ['all', ...choirNames];
  };

  // Filter choirs based on active filter
  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredChoirs(choirs);
    } else {
      setFilteredChoirs(choirs.filter(choir => choir.name === activeFilter));
    }
  }, [activeFilter, choirs]);

  const getFilterLabel = (filter: string): string => {
    if (filter === 'all') return 'All Choirs';
    return filter; // Use choir name as-is
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-lg">Loading choirs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
        <div className="text-center text-white">
          <p className="text-lg">{error}</p>
        </div>
      </div>
    );
  }

  if (choirs.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-500 to-primary-700">
        <div className="bg-gradient-to-br from-primary-500 to-primary-700">
          <div className="max-w-6xl mx-auto px-8 py-12">
            <div className="text-center text-white">
              <h1 className="text-5xl font-bold mb-4 drop-shadow-lg">Our Choirs</h1>
              <p className="text-xl opacity-90 max-w-2xl mx-auto">
                Discover the talented choirs that make our school's music program special.
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white py-12">
          <div className="max-w-6xl mx-auto px-8 text-center">
            <Music size={64} className="mx-auto text-gray-400 mb-6" />
            <h3 className="text-2xl font-semibold text-secondary-800 mb-4">No Choirs Available</h3>
            <p className="text-secondary-600 text-lg">This school hasn't added any choirs yet.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-8 py-8">
      <div className="bg-white rounded-2xl p-8 shadow-lg">
        {/* Clean header matching the events page style */}
        <h2 className="text-3xl font-bold text-secondary-800 mb-6 border-b-4 border-primary-500 pb-2">
          Our Choirs
        </h2>

        {/* Filter Tabs */}
        <div className="flex justify-center gap-4 mb-12 flex-wrap">
          {getFilterTabs().map((filter) => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                activeFilter === filter
                  ? 'bg-primary-500 text-white shadow-lg transform -translate-y-1'
                  : 'bg-primary-50 text-primary-600 border-2 border-primary-200 hover:bg-primary-500 hover:text-white hover:transform hover:-translate-y-1'
              }`}
            >
              {getFilterLabel(filter)}
            </button>
          ))}
        </div>

        {/* Choirs Grid - back to original beautiful card design */}
        <div className="space-y-8">
          {filteredChoirs.map((choir, index) => {
            const isEven = index % 2 === 1;
            const hasImages = choir.images && choir.images.length > 0;

            return (
              <div
                key={choir.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className={`grid md:grid-cols-2 min-h-96 ${isEven ? 'md:grid-flow-dense' : ''}`}>
                  {/* Image Section */}
                  <div className={`relative overflow-hidden ${isEven ? 'md:col-start-2' : ''}`}>
                    {hasImages ? (
                      <ImageCarousel images={choir.images} alt={choir.name} />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/80 to-primary-700/80"></div>
                        <Music size={64} className="text-white relative z-10" />
                      </div>
                    )}
                  </div>

                  {/* Content Section */}
                  <div className={`p-8 flex flex-col justify-center ${isEven ? 'md:col-start-1' : ''}`}>
                    <div className="choir-header mb-6">
                      <h2 className="text-3xl font-bold text-secondary-800 mb-4">{choir.name}</h2>
                      <div className="flex gap-3 mb-4 flex-wrap">
                        {choir.memberCount && (
                          <span className="bg-primary-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                            <Users size={14} />
                            {choir.memberCount} Members
                          </span>
                        )}
                        {choir.skillLevel && (
                          <span className="bg-primary-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                            <Star size={14} />
                            {choir.skillLevel}
                          </span>
                        )}
                        <span className="bg-primary-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                          <Clock size={14} />
                          {choir.auditionRequired ? 'Audition Required' : 'No Audition'}
                        </span>
                      </div>
                    </div>

                    <p className="text-secondary-600 leading-relaxed mb-6">
                      {choir.description}
                    </p>

                    {/* Testimonial */}
                    {choir.testimonialText && choir.testimonialAuthor && (
                      <div className="bg-secondary-50 p-4 rounded-lg border-l-4 border-primary-500">
                        <p className="text-secondary-600 italic mb-2">"{choir.testimonialText}"</p>
                        <p className="text-secondary-800 font-medium text-sm">- {choir.testimonialAuthor}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ChoirDisplay;
